import { ReturnProduct } from "@redotech/redo-model/draft-return/draft-return-items";
import { weightUnitToGrams } from "@redotech/shopify-client/utils";
import { mockGetCollectionProductsPaginatedQueryResponse } from "./mock-responses/collections";
import { ProductInfo } from "./products";
import {
  GetCollectionProductsPaginatedQuery,
  GetCollectionProductsPaginatedQueryVariables,
} from "./shopify-storefront.graphql";
import { ShopifyStorefrontClientInterface } from "./storefront-client";

const getCollectionProductsPaginatedQuery = /* GraphQL */ `
  query GetCollectionProductsPaginated(
    $collectionID: ID!
    $first: Int!
    $after: String
  ) {
    collection(id: $collectionID) {
      products(first: $first, after: $after) {
        nodes {
          id
          title
          tags
          productType
          options {
            id
            name
            values
          }
          featuredImage {
            altText
            url
          }
          variants(first: 100) {
            nodes {
              id
              sku
              title
              weight
              weightUnit
              quantityAvailable
              selectedOptions {
                name
                value
              }
              price {
                amount
                currencyCode
              }
              image {
                url
              }
            }
          }
          collections(first: 10) {
            nodes {
              title
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

export const productInfoToReturnProduct = (
  product: ProductInfo,
): ReturnProduct | null => {
  const primaryVariant = product.variants?.nodes[0];
  if (!primaryVariant) {
    return null;
  }
  return {
    id: primaryVariant?.id,
    title: product.title,
    variantTitle: primaryVariant.title,
    variantId: primaryVariant.id,
    image: product.featuredImage?.url ?? "",
    price: primaryVariant.price.amount,
    currency: primaryVariant.price.currencyCode,
    productTags: product.tags,
    returnValue: primaryVariant.price.amount,
    sku: primaryVariant.sku ?? "",
    productId: product.id.split("/").slice(-1)[0],
    deliveredOn: new Date(),
    fulfilledOn: new Date(),
    lastFulfillmentUpdateOn: new Date(),
    productDiscounts: [],
    productDiscountedAmount: 0,
    variantData: {
      productTitle: product.title,
      tags: product.tags,
      options: product.options.map((option, index) => ({
        position: index,
        name: option.name,
        values: option.values,
      })),
      variants: product.variants.nodes.map((variant) => ({
        id: variant.id,
        sku: variant.sku,
        title: variant.title,
        displayName: variant.title,
        price: variant.price.amount,
        image: variant.image?.url,
        selectedOptions: variant.selectedOptions,
        inventoryTracked: false,
        inventoryQuantity: variant.quantityAvailable ?? 0,
        inventoryPolicy:
          "DENY" as ReturnProduct["variantData"]["variants"][0]["inventoryPolicy"],
      })),
    },
    productType: product.productType,
    collections: product.collections.nodes.map(
      (collection) => collection.title,
    ),
    productVariantMetafields: {},
    grams:
      weightUnitToGrams({
        unit: primaryVariant.weightUnit,
        value: primaryVariant.weight,
      }) ?? 1,
    tax: "0",
  };
};

export async function getCollectionProductsPaginated(
  client: ShopifyStorefrontClientInterface,
  {
    collectionId,
    cursor,
    pageSize,
  }: { collectionId: string; cursor: string | undefined; pageSize: number },
): Promise<{
  products: ReturnProduct[];
  cursor: string | undefined;
  hasNextPage: boolean;
}> {
  const { data, errors } = await client.request<
    GetCollectionProductsPaginatedQuery,
    GetCollectionProductsPaginatedQueryVariables
  >(
    getCollectionProductsPaginatedQuery,
    { collectionID: collectionId, first: pageSize, after: cursor },
    mockGetCollectionProductsPaginatedQueryResponse,
  );

  if (errors) {
    throw errors;
  }

  const pageInfo = data?.collection?.products?.pageInfo;
  const products = (data?.collection?.products?.nodes ?? []).reduce<
    ReturnProduct[]
  >((acc, product) => {
    const returnProduct = productInfoToReturnProduct(product as ProductInfo);
    if (!returnProduct) {
      return acc;
    }
    return [...acc, returnProduct];
  }, []);

  return {
    products,
    cursor: pageInfo?.endCursor ?? undefined,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
}
